import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ToastContainer } from 'react-toastify';
import { PuffLoader } from 'react-spinners';
import FiltersBar from './FiltersBar.jsx';
import useUserActivitiesData from '../../hooks/useUserActivitiesData.js';
import UserActivityService from '../../Services/userActivity.service.js';
import ActivityGraphs from './ActivityGraphs.jsx';
import ActivityTables from './ActivityTables.jsx';
import AttendanceView from './AttendanceView.jsx';
import MouseControlView from './MouseControlView.jsx';
import EfficiencyReportView from './EfficiencyReportView.jsx';
import ClientActivityReportView from './ClientActivityReportView.jsx';
import DownloadReportModal from './DownloadReportModal.jsx';
import EfficiencyDownloadModal from './EfficiencyDownloadModal.jsx';
import ReportModal from './ReportModal.jsx';
import { useLocation } from 'react-router-dom';

export default function UserActivities({ user, token, direction }) {
  const { subAccountants, clients, activities, loading: filtersLoading } =
    useUserActivitiesData(user, token);

  const [viewType, setViewType] = useState('graph');
  const [client, setClient] = useState(null);
  const [filters, setFilters] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [clientAdded, setClientAdded] = useState(false);

  const [dailySummaries, setDailySummaries] = useState({ summaries: [], includedIds: [] });
  const [userTotals, setUserTotals] = useState([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEfficiencyDownloadModalOpen, setIsEfficiencyDownloadModalOpen] = useState(false);
  const [efficiencyReportType, setEfficiencyReportType] = useState('employee');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportModalData, setReportModalData] = useState({ title: '', content: '' });
  const location = useLocation();

  const handleEfficiencyClick = () => {
    setEfficiencyReportType('employee');
    setIsEfficiencyDownloadModalOpen(true);
  };

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  useEffect(() => {
    if (!filters || !token || !user?.accountantId) return;

    let cancelled = false;

    async function loadSummaries() {
      setDataLoading(true);
      try {
        const [summariesResp, userResp] = await Promise.all([
          UserActivityService.GetDailySummaries(user.accountantId, filters, token),
          UserActivityService.GetDailyByUser(
            { ...filters, accountantId: user.accountantId },
            token
          ),
        ]);

        if (cancelled) return;

        const summariesJson = await summariesResp.json();
        setDailySummaries(summariesJson);

        const userJson = await userResp.json();
        setUserTotals(userJson.totals || userJson);
      } catch (err) {
        console.error('Failed to load activity data', err);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    }

    loadSummaries();

    if (location.state?.client && !clientAdded) {
      setClient(location.state.client);
    }

    return () => { cancelled = true; };
  }, [filters, token, user?.accountantId]);

  useEffect(() => {
    if (!clientAdded && client) {
      setFilters(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          clientCaseIds: [
            ...(prev.clientCaseIds || []),
            client?.caseId
          ]
        };
      });
      setClientAdded(true);
    }
  }, [client]);

  const changeViewType = (newView) => {
    setViewType(newView);
  };

  const showLoader = filtersLoading || dataLoading;

  return (
    <>
      <ToastContainer />
      {showLoader && (
        <div className="loader-container">
          <PuffLoader size={80} loading color="#667eea" />
        </div>
      )}

      <div className={`user-activity-page ${direction}`}>
        <FiltersBar
          subAccountants={subAccountants}
          clients={clients}
          activities={activities}
          direction={direction}
          onFilterChange={handleFilterChange}
          onViewChange={changeViewType}
          onDownloadClick={() => setIsDownloadModalOpen(true)}
          onEmployeeEfficiencyClick={handleEfficiencyClick}
          clientFromCrm={client?.caseId}
        />

        <DownloadReportModal
          open={isDownloadModalOpen}
          onClose={() => setIsDownloadModalOpen(false)}
          user={user}
          token={token}
          direction={direction}
        />

        <EfficiencyDownloadModal
          open={isEfficiencyDownloadModalOpen}
          onClose={() => setIsEfficiencyDownloadModalOpen(false)}
          user={user}
          token={token}
          direction={direction}
          reportType={efficiencyReportType}
        />

        <ReportModal
          open={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          title={reportModalData.title}
          htmlContent={reportModalData.content}
          direction={direction}
        />

        {filters && (
          <>
            {viewType === 'graph' && (
              <ActivityGraphs
                summaries={dailySummaries.summaries}
                direction={direction}
              />
            )}
            {viewType === 'table' && (
              <ActivityTables
                subAccountants={subAccountants}
                clients={clients}
                summaries={dailySummaries.summaries}
                userTotals={userTotals?.summariesByDay}
                direction={direction}
              />
            )}
            {viewType === 'attendance' && (
              <AttendanceView
                accountantId={user.accountantId}
                filters={filters}
                token={token}
                direction={direction}
                subAccountants={subAccountants}
              />
            )}
            {viewType === 'mouse' && (
              <MouseControlView
                accountantId={user.accountantId}
                filters={filters}
                token={token}
                direction={direction}
                subAccountants={subAccountants}
              />
            )}
            {viewType === 'efficiency' && (
              <EfficiencyReportView
                accountantId={user.accountantId}
                filters={filters}
                token={token}
                direction={direction}
                subAccountants={subAccountants}
              />
            )}
            {viewType === 'clientReport' && (
              <ClientActivityReportView
                accountantId={user.accountantId}
                filters={filters}
                token={token}
                direction={direction}
                clients={clients}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

UserActivities.propTypes = {
  user: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  direction: PropTypes.oneOf(['ltr', 'rtl']).isRequired,
};
